import { useState, useEffect } from 'react';
import { Menu, X, Anchor } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

export const Navigation = () => {
  const { language, setLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navLinks = [
    { name: t('schedule'), href: '#schedule' },
    { name: t('whereToStay'), href: '#accommodations' },
    { name: t('explore'), href: '#explore' },
    { name: t('whereToEat'), href: '#eat' },
    { name: t('faq'), href: '#faq' },
    { name: t('registry'), href: '#registry' },
    { name: t('gallery'), href: '#gallery' },
  ];

  const getLinkProps = (link) => ({
    href: link.href,
    ...(link.external
      ? { target: '_blank', rel: 'noopener noreferrer' }
      : { onClick: (e) => scrollTo(e, link.href) }),
    ...(link.href === '#registry' ? { 'data-testid': 'nav-registry' } : {}),
  });

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleScroll is defined inside this effect, not an external dependency
  }, []);

  const scrollTo = (e, href) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#EFF4F9]/95 backdrop-blur-sm shadow-sm py-3' : 'bg-[#EFF4F9] py-4'
      }`} data-testid="navigation">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between">
          <a href="#home" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="flex items-center gap-2 font-serif text-xl text-[#1A2B3D]" data-testid="nav-logo">
            <Anchor className="w-4 h-4 text-[#C9A96E]" strokeWidth={1.5} />
            A & T
          </a>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a key={link.href} {...getLinkProps(link)}
                className="text-sm text-[#1A2B3D] hover:text-[#C9A96E] transition-colors"
              >{link.name}</a>
            ))}
            <a href="#rsvp" onClick={(e) => scrollTo(e, '#rsvp')}
              className="bg-[#1E3A5F] text-white text-sm px-5 py-2 rounded hover:bg-[#2C5F7C] transition-colors"
              data-testid="nav-rsvp-button">RSVP</a>
          </div>

          <div className="hidden md:flex items-center gap-1.5 ml-4">
            <button onClick={() => setLanguage('fr')}
              className={`px-3 py-1 rounded-full text-sm transition-all ${language === 'fr' ? 'bg-[#1E3A5F] text-white font-medium' : 'text-[#6B8299] hover:text-[#1A2B3D]'}`}
              data-testid="lang-fr">FR</button>
            <button onClick={() => setLanguage('en')}
              className={`px-3 py-1 rounded-full text-sm transition-all ${language === 'en' ? 'bg-[#1E3A5F] text-white font-medium' : 'text-[#6B8299] hover:text-[#1A2B3D]'}`}
              data-testid="lang-en">EN</button>
          </div>

          <div className="flex items-center gap-2 md:hidden">
            <button onClick={() => setLanguage('fr')}
              className={`px-2.5 py-1 rounded-full text-xs transition-all ${language === 'fr' ? 'bg-[#1E3A5F] text-white font-medium' : 'text-[#6B8299] bg-white'}`}
              data-testid="lang-fr-mobile">FR</button>
            <button onClick={() => setLanguage('en')}
              className={`px-2.5 py-1 rounded-full text-xs transition-all ${language === 'en' ? 'bg-[#1E3A5F] text-white font-medium' : 'text-[#6B8299] bg-white'}`}
              data-testid="lang-en-mobile">EN</button>
            <button className="p-2 text-[#1A2B3D]" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-button">
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-[#EFF4F9] flex flex-col items-center justify-center" data-testid="mobile-menu">
            <div className="flex items-center gap-2 mb-8">
              <button onClick={() => setLanguage('fr')}
                className={`px-4 py-1.5 rounded-full text-lg transition-all ${language === 'fr' ? 'bg-[#1E3A5F] text-white font-medium' : 'text-[#6B8299]'}`}>FR</button>
              <button onClick={() => setLanguage('en')}
                className={`px-4 py-1.5 rounded-full text-lg transition-all ${language === 'en' ? 'bg-[#1E3A5F] text-white font-medium' : 'text-[#6B8299]'}`}>EN</button>
            </div>
            <div className="flex flex-col items-center gap-6">
              {navLinks.map((link, i) => (
                <motion.a key={link.href} {...getLinkProps(link)}
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                  className="font-serif text-2xl text-[#1A2B3D] hover:text-[#C9A96E] transition-colors"
                >{link.name}</motion.a>
              ))}
              <motion.a href="#rsvp" onClick={(e) => scrollTo(e, '#rsvp')}
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
                className="mt-4 bg-[#1E3A5F] text-white text-lg px-8 py-3 rounded hover:bg-[#2C5F7C] transition-colors"
              >RSVP</motion.a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
